import { useState, useRef, useEffect } from 'react';

import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
// material-ui
import { useTheme } from '@mui/material/styles';
import {
  Chip,
  ClickAwayListener,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Popper,
  Typography
} from '@mui/material';

// project imports
import MainCard from 'ui-component/cards/MainCard';
import Transitions from 'ui-component/extended/Transitions';
import HotelBusinessAvatar from 'ui-component/hotel/HotelBusinessAvatar';
import useLogin from 'hooks/useLogin';

// assets
import { IconLogout, IconSettings, IconUserScan } from '@tabler/icons-react';

// ==============================|| PROFILE MENU ||============================== //

const ProfileSection = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const customization = useSelector((state) => state.customization);
  const account = useSelector((state) => state.account);
  const isHotel = account.user?.biz_role === 'hotel';
  const { logout } = useLogin();

  const [open, setOpen] = useState(false);
  /**
   * anchorRef is used on different componets and specifying one type leads to other components throwing an error
   * */
  const anchorRef = useRef(null);
  const handleLogout = async () => {
    logout();
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const prevOpen = useRef(open);
  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current.focus();
    }

    prevOpen.current = open;
  }, [open]);

  return (
    <>
      <Chip
        sx={{
          height: '48px',
          alignItems: 'center',
          borderRadius: isHotel ? '6px' : '27px',
          transition: 'all .2s ease-in-out',
          borderColor: isHotel ? '#DDE4E0' : theme.typography.menuChip.background,
          backgroundColor: isHotel ? '#F0F5F2' : theme.typography.menuChip.background,
          '&[aria-controls="menu-list-grow"], &:hover': {
            borderColor: isHotel ? '#9DBFB7' : theme.palette.primary.main,
            background: `${isHotel ? '#E5F3F0' : theme.palette.primary.main}!important`,
            color: isHotel ? '#147D72' : theme.palette.primary.light,
            '& svg': {
              stroke: isHotel ? '#147D72' : theme.palette.primary.light
            }
          },
          '& .MuiChip-label': {
            lineHeight: 0,
            [theme.breakpoints.down('sm')]: {
              display: 'none'
            }
          },
          [theme.breakpoints.down('sm')]: {
            height: '42px',
            paddingRight: 0
          }
        }}
        icon={
          <HotelBusinessAvatar
            size={32}
            sx={{
              margin: '8px 0 8px 8px !important',
              cursor: 'pointer'
            }}
          />
        }
        label={<IconSettings stroke={1.5} size="1.5rem" color={isHotel ? '#147D72' : theme.palette.primary.main} />}
        variant="outlined"
        ref={anchorRef}
        aria-controls={open ? 'menu-list-grow' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
        color="primary"
      />
      <Popper
        placement="bottom-end"
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 14]
              }
            }
          ]
        }}
      >
        {({ TransitionProps }) => (
          <Transitions in={open} {...TransitionProps}>
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard
                  border={isHotel}
                  elevation={isHotel ? 0 : 16}
                  content={false}
                  boxShadow={!isHotel}
                  shadow={isHotel ? 'none' : theme.shadows[16]}
                >
                  <List
                    component="nav"
                    sx={{
                      width: '100%',
                      maxWidth: 350,
                      minWidth: 150,
                      backgroundColor: theme.palette.background.paper,
                      borderRadius: isHotel ? '6px' : '10px',
                      [theme.breakpoints.down('md')]: {
                        minWidth: '100%'
                      },
                      '& .MuiListItemButton-root': {
                        mt: 0.5
                      }
                    }}
                  >
                    <ListItemButton
                      sx={{
                        borderRadius: isHotel ? '4px' : `${customization.borderRadius}px`,
                        ...(isHotel && { '&:hover': { bgcolor: '#F0F5F2', color: '#147D72' } })
                      }}
                      onClick={() => navigate('/panel/profile')}
                    >
                      <ListItemIcon>
                        <IconUserScan stroke={1.5} size="1.3rem" />
                      </ListItemIcon>
                      <ListItemText primary={<Typography variant="body2">{isHotel ? '我的账号' : '设置'}</Typography>} />
                    </ListItemButton>

                    <ListItemButton
                      sx={{
                        borderRadius: isHotel ? '4px' : `${customization.borderRadius}px`,
                        ...(isHotel && { '&:hover': { bgcolor: '#F0F5F2' } })
                      }}
                      onClick={handleLogout}
                    >
                      <ListItemIcon>
                        <IconLogout stroke={1.5} size="1.3rem" />
                      </ListItemIcon>
                      <ListItemText primary={<Typography variant="body2">{isHotel ? '退出登录' : '登出'}</Typography>} />
                    </ListItemButton>
                  </List>
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </>
  );
};

export default ProfileSection;
